import type { ColumnSchema } from './inferSchema'

export type ChartType = 'bar' | 'trend' | 'mix'

export interface ChartSpec {
  type: ChartType
  xCol: string
  yCol?: string
  label: string
}

function isId(key: string, sample: Record<string, unknown>[]): boolean {
  // Only exclude string columns — numeric IDs like order sequences should still chart
  const vals = sample.map(r => String(r[key] ?? '')).filter(Boolean)
  const unique = new Set(vals)
  const isStringLike = sample.some(r => typeof r[key] === 'string')
  return isStringLike && vals.length > 10 && unique.size / vals.length > 0.9
}

function isFreeText(key: string, sample: Record<string, unknown>[]): boolean {
  const vals = sample.map(r => String(r[key] ?? ''))
  const avgLen = vals.reduce((s, v) => s + v.length, 0) / (vals.length || 1)
  return avgLen > 30
}

export function classifyColumns(
  schema: ColumnSchema[],
  rows: Record<string, unknown>[]
): ChartSpec[] {
  if (rows.length === 0 || schema.length === 0) return []

  const sample = rows.slice(0, 100).filter(Boolean)
  const specs: ChartSpec[] = []
  const usedXCols = new Set<string>()
  const usedYCols = new Set<string>()

  function uniqueCount(key: string) {
    return new Set(sample.map(r => String(r[key] ?? '')).filter(Boolean)).size
  }

  const numericCols = schema.filter(s =>
    (s.kind === 'number' || s.kind === 'currency') && !isId(s.key, sample)
  )
  const dateCols = schema.filter(s => s.kind === 'date')
  const enumCols = schema.filter(s =>
    s.kind === 'enum' && !isId(s.key, sample) && uniqueCount(s.key) >= 2
  )
  const textCols = schema.filter(s =>
    s.kind === 'text' && !isId(s.key, sample) && !isFreeText(s.key, sample)
  )

  const lowCardTextCols = textCols.filter(s => {
    const vals = sample.map(r => String(r[s.key] ?? '')).filter(Boolean)
    const unique = new Set(vals)
    return unique.size >= 2 && unique.size <= 20
  })

  const categoricalCols = [...enumCols, ...lowCardTextCols]

  // 1. Trend: date × best numeric
  for (const dateCol of dateCols) {
    if (specs.length >= 3) break
    const numCol = numericCols.find(n => !usedYCols.has(n.key))
    if (!numCol) continue
    specs.push({ type: 'trend', xCol: dateCol.key, yCol: numCol.key, label: `${numCol.key} over time` })
    usedXCols.add(dateCol.key)
    usedYCols.add(numCol.key)
    break
  }

  // 2. Bar: categorical × numeric
  for (const catCol of categoricalCols) {
    if (specs.length >= 3) break
    if (usedXCols.has(catCol.key)) continue
    const numCol = numericCols.find(n => !usedYCols.has(n.key))
    if (!numCol) continue
    specs.push({ type: 'bar', xCol: catCol.key, yCol: numCol.key, label: `${numCol.key} by ${catCol.key}` })
    usedXCols.add(catCol.key)
    usedYCols.add(numCol.key)
  }

  // 3. Mix: enum/category distribution
  for (const catCol of categoricalCols) {
    if (specs.length >= 3) break
    if (usedXCols.has(catCol.key)) continue
    specs.push({ type: 'mix', xCol: catCol.key, label: `${catCol.key} distribution` })
    usedXCols.add(catCol.key)
  }

  return specs
}

export function aggregateBar(
  rows: Record<string, unknown>[],
  xCol: string,
  yCol: string
): Array<{ label: string; value: number }> {
  const sums = new Map<string, number>()
  for (const row of rows) {
    const x = String(row[xCol] ?? '(blank)')
    const y = parseFloat(String(row[yCol] ?? '0')) || 0
    sums.set(x, (sums.get(x) ?? 0) + y)
  }
  return Array.from(sums.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
}

export function aggregateTrend(
  rows: Record<string, unknown>[],
  xCol: string,
  yCol: string
): Array<{ label: string; value: number }> {
  const sums = new Map<string, number>()
  for (const row of rows) {
    const raw = String(row[xCol] ?? '')
    const bucket = raw.length >= 7 ? raw.slice(0, 7) : raw
    const y = parseFloat(String(row[yCol] ?? '0')) || 0
    sums.set(bucket, (sums.get(bucket) ?? 0) + y)
  }
  return Array.from(sums.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export function aggregateMix(
  rows: Record<string, unknown>[],
  xCol: string
): Array<{ label: string; value: number }> {
  const counts = new Map<string, number>()
  for (const row of rows) {
    const x = String(row[xCol] ?? '(blank)')
    counts.set(x, (counts.get(x) ?? 0) + 1)
  }
  return Array.from(counts.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
}
